import React, { useState, useEffect, useCallback } from 'react';
import { isCvUnread, CV_UNREAD_LABEL } from '../../../utils/cvRead';
import StatusDropdown from './StatusDropdown';
import Pagination from './Pagination';
import ResumeViewer from '../resume/ResumeViewer';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { Eye, Edit2, Send, RefreshCw, RotateCcw, Download } from 'lucide-react';
import { resumeApi } from '../../../services/resumeApi';
import OfferLetterModal from '../offer/OfferLetterModal';
import { Modal } from 'antd';
import InterviewDetailsModal from '../modal/InterviewDetailsModal';
import api from '../../../services/api';
import config from '../../../config/config.js';
import { APP_BASE } from '../../../config/base';
import { briefScoresHtml, hasBriefScores } from '../../../features/assessments/BriefReportScores';

// ── Selection Status Dropdown — lets HR manually override a candidate's status ──
function SelectionStatusDropdown({ resumeId, current, statusType = 'finalInterview', onChanged }) {
  const [saving, setSaving] = React.useState(false);
  const [value, setValue]   = React.useState(current || 'not_started');

  const STATUS_OPTIONS = [
    { value: 'shortlisted', label: '✓ Shortlisted' },
    { value: 'rejected',    label: '✗ Rejected'    },
    { value: 'completed',   label: '● Completed'   },
    { value: 'not_started', label: '○ Not Started' },
  ];

  const COLOR = {
    shortlisted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rejected:    'bg-red-50    text-red-600    border-red-200',
    completed:   'bg-blue-50   text-blue-600   border-blue-200',
    not_started: 'bg-gray-50   text-gray-500   border-gray-200',
  };

  const handleChange = async (newVal) => {
    if (newVal === value) return;
    try {
      setSaving(true);
      await resumeApi.updateResumeStatus(resumeId, statusType, newVal, true);
      setValue(newVal);
      if (onChanged) onChanged(newVal);
      toast.success('Status updated');
    } catch {
      toast.error('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative">
      <select
        value={value}
        onChange={e => handleChange(e.target.value)}
        disabled={saving}
        className={`text-xs font-semibold border rounded-lg px-2.5 py-1.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:opacity-60 transition-colors ${COLOR[value] || COLOR.not_started}`}
      >
        {STATUS_OPTIONS.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

const ResumesContent = ({ 
  resumes = [], 
  pagination, 
  setPagination, 
  onPageChange, 
  currentFilter,
  screenType = 'resumeScreening',
  onStatusChange,
  onDelete,
  onInvite,
  onViewQuestions,
  onGenerateQuestions,
  generatingForResume,
  jobDetails,
  showDelete = true,
  onScoreUpdate,
  onGenerateOffer,
  onSendOffer,
  onEditOffer,
  loading,
  generatingOffer,
  onViewOffer,
  onViewFeedback,
  onReprocess,
  onRefresh,
  pipelineStages,   // optional: full pipeline stage list (from OfferLetterPage)
}) => {
  const params = useParams();
  const { id } = useParams();
  const {id:jobId} = useParams();
  console.log('params', params);
  console.log('id', id);
  console.log('jobId', jobId);
  const [selectedResume, setSelectedResume] = useState(null);
  const [showResumeViewer, setShowResumeViewer] = useState(false);
  const [editingScore, setEditingScore] = useState(null);
  const [scoreInput, setScoreInput] = useState('');
  const [showOfferLetter, setShowOfferLetter] = useState(false);
  const [selectedOfferLetter, setSelectedOfferLetter] = useState(null); // { content, company }
  const [editOfferState, setEditOfferState] = useState(null); // { resumeId, content }
  const [savingOffer, setSavingOffer] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(null); // resumeId

  // ── Names of legacy rounds — used to deduplicate pipeline stage results ─────
  const LEGACY_ROUND_LABELS = new Set([
    'pre-screening', 'pre screening', 'prescreening',
    'psychoanalytical', 'psychoanalytical assessment',
    'final interview', 'finalinterview',
    'coding', 'coding test',
  ]);
  const isLegacyDuplicate = (label) =>
    LEGACY_ROUND_LABELS.has((label || '').toLowerCase().trim());

  // ── Extract pipeline-only stages: completed, labelled, not duplicate ─────────
  const getPipelineStages = (resume) =>
    (resume.pipelineStageResults || []).filter(p =>
      p.label &&                          // must have a label
      p.inviteStatus === 'completed' &&   // must be completed
      !isLegacyDuplicate(p.label)         // not already covered by legacy round
    );

  // ── Compute overall score across every distinct scored round / stage ─────────
  const computeOverallScore = (resume) => {
    const scores = [];
    if (resume.aiScore > 0)               scores.push(resume.aiScore);
    if (resume.preScreeningScore > 0)     scores.push(resume.preScreeningScore);
    if (resume.psychoanalyticalScore > 0) scores.push(resume.psychoanalyticalScore);
    if (resume.finalInterviewScore > 0)   scores.push(resume.finalInterviewScore);
    if (resume.codingScore > 0)           scores.push(resume.codingScore);
    getPipelineStages(resume).forEach(p => {
      if (p.score != null && p.score > 0) scores.push(p.score);
    });
    if (!scores.length) return null;
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  };

  const handleDownloadReport = async (resume) => {
    try {
      setDownloadingReport(resume._id);

      // Fetch the FULL resume (unrestricted) so we always have pipelineStageResults
      const [fullResumeRes, interviewRes, costsRes] = await Promise.all([
        api.get(`/resumes/details/${resume._id}`).catch(() => ({ data: { data: null } })),
        api.get(`/reports/resume/${resume._id}`).catch(() => ({ data: { data: [] } })),
        api.get(`/reports/candidate/${resume._id}/costs`).catch(() => ({ data: { data: null } })),
      ]);
      // Merge: prefer full fetch, fall back to table row
      const fullResume = {
        ...resume,
        ...(fullResumeRes?.data?.data || {}),
      };
      const reports   = interviewRes.data?.data || [];
      const costsData = costsRes.data?.data || null;


      const ROUND_LABEL = { preScreening: 'Pre-Screening', psychoanalytical: 'Psychoanalytical', finalInterview: 'Final Interview', coding: 'Coding' };
      const RECO_COLOR  = { strongly_recommend: '#1D7A46', recommend: '#075358', neutral: '#f5a623', not_recommend: '#d0021b', strongly_not_recommend: '#5a010c' };

      // ── Build comprehensive score rows from the FULL resume ───────────────────
      const scoreRows = [];
      if (fullResume.aiScore > 0)
        scoreRows.push({ label: 'Resume Screening (ATS)', score: fullResume.aiScore, status: fullResume.resumeScreeningStatus });
      if (fullResume.preScreeningScore > 0)
        scoreRows.push({ label: 'Pre-Screening Interview', score: fullResume.preScreeningScore, status: fullResume.preScreeningStatus });
      if (fullResume.psychoanalyticalScore > 0)
        scoreRows.push({ label: 'Psychoanalytical Assessment', score: fullResume.psychoanalyticalScore, status: fullResume.psychoanalyticalStatus });
      if (fullResume.finalInterviewScore > 0)
        scoreRows.push({ label: 'Final Interview', score: fullResume.finalInterviewScore, status: fullResume.finalInterviewStatus });
      if (fullResume.codingScore > 0)
        scoreRows.push({ label: 'Coding Test', score: fullResume.codingScore, status: fullResume.codingStatus });
      getPipelineStages(fullResume).forEach(p => {
        scoreRows.push({
          label:     p.label,
          score:     p.score,
          status:    p.status,
          earned:    p.examDetails?.earnedPoints,
          total:     p.examDetails?.totalPoints,
          proctor:   p.examDetails?.proctoringScore,
          integrity: p.examDetails?.integrityScore,
          risk:      p.examDetails?.riskLevel,
          violations: p.examDetails?.violationCount,
          completedAt: p.completedAt,
        });
      });

      const overallAvg = computeOverallScore(fullResume);
      const parsed     = fullResume.analysis?.parsedData || {};

      // ── Helpers ───────────────────────────────────────────────────────────────
      const scoreColor  = (s) => s >= 75 ? '#1D7A46' : s >= 50 ? '#f5a623' : '#d0021b';
      const scoreBg     = (s) => s >= 75 ? '#DDF2E4' : s >= 50 ? '#fdf2d7' : '#fde2e4';
      const statusBadge = (st) => {
        const map = { shortlisted:'#1D7A46', rejected:'#d0021b', completed:'#075358', not_started:'#b5b1b2', manual_review:'#f5a623' };
        return `<span style="background:${map[st]||'#b5b1b2'}20;color:${map[st]||'#667683'};padding:3px 10px;border-radius:99px;font-size:11px;font-weight:700">${(st||'—').replace(/_/g,' ')}</span>`;
      };
      const riskBadge = (rk) => {
        const map = { low:'#1D7A46', medium:'#f5a623', high:'#d0021b' };
        return rk ? `<span style="background:${map[rk]||'#b5b1b2'}20;color:${map[rk]||'#667683'};padding:3px 10px;border-radius:99px;font-size:11px;font-weight:700">${rk} risk</span>` : '';
      };
      const recoBadge = (r) => {
        const map = { strong_hire:'#1D7A46', hire:'#075358', borderline:'#f5a623', no_hire:'#d0021b', strong_no_hire:'#5a010c', time_expired:'#b5b1b2' };
        return r ? `<span style="background:${map[r]||'#b5b1b2'}20;color:${map[r]||'#667683'};padding:4px 14px;border-radius:99px;font-size:12px;font-weight:700;text-transform:capitalize">${r.replace(/_/g,' ')}</span>` : '';
      };

      // ── Overall recommendation from last/best interview report ────────────────
      const lastRep = reports[0] || null;

      const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>Hiring Report — ${fullResume.candidateName || fullResume.email}</title>
      <style>
        *{box-sizing:border-box}
        body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;max-width:900px;margin:0 auto;padding:36px 32px;color:#383435;background:#fff}
        h1{font-size:26px;font-weight:900;margin:0 0 2px}
        h2{font-size:15px;font-weight:800;color:#1E2A33;margin:0 0 14px;text-transform:uppercase;letter-spacing:.04em}
        .chip{display:inline-block;padding:3px 12px;border-radius:99px;font-size:11px;font-weight:700}
        .section{margin-bottom:22px;border:1px solid #DCE5E8;border-radius:12px;overflow:hidden}
        .sec-title{background:#F5F8FA;padding:10px 16px;font-size:12px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;color:#40505C;border-bottom:1px solid #DCE5E8}
        .sec-body{padding:16px}
        .bar-wrap{height:6px;background:#DCE5E8;border-radius:99px;margin-top:4px}
        .bar{height:6px;border-radius:99px}
        .divider{border:none;border-top:2px dashed #DCE5E8;margin:28px 0}
        .narrative{line-height:1.75;color:#40505C;font-size:13.5px}
        table{width:100%;border-collapse:collapse;font-size:13px}
        th{text-align:left;padding:8px 12px;color:#40505C;font-weight:700;background:#F5F8FA}
        td{padding:8px 12px;border-top:1px solid #efeeea}
        .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px 24px}
        .info-item label{display:block;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#b5b1b2;margin-bottom:2px}
        .info-item span{font-size:13px;font-weight:600;color:#383435}
        .timeline{display:flex;flex-direction:column;gap:0}
        .tl-row{display:flex;align-items:flex-start;gap:12px;padding:10px 0;border-bottom:1px solid #efeeea}
        .tl-row:last-child{border-bottom:none}
        .tl-dot{width:10px;height:10px;border-radius:50%;margin-top:4px;flex-shrink:0}
        @media print{body{padding:16px}.no-print{display:none}}
      </style></head><body>

      <!-- ══ HEADER ══ -->
      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:6px">
        <div>
          <h1>${fullResume.candidateName || fullResume.email || 'Candidate'}</h1>
          <div style="font-size:13px;color:#667683;margin-top:2px">
            ${fullResume.email || ''}
            ${fullResume.phone ? ` · ${fullResume.phone}` : ''}
            ${parsed.location ? ` · ${parsed.location}` : ''}
          </div>
        </div>
        <div style="text-align:right">
          ${overallAvg != null ? `<div style="font-size:38px;font-weight:900;color:${scoreColor(overallAvg)};line-height:1">${overallAvg}%</div><div style="font-size:11px;color:#b5b1b2;font-weight:600">OVERALL SCORE</div>` : ''}
        </div>
      </div>
      <div style="font-size:11px;color:#b5b1b2;margin-bottom:24px">
        Generated ${new Date().toLocaleString()}
        ${lastRep?.recommendation ? ` · ${recoBadge(lastRep.recommendation)}` : ''}
      </div>

      <!-- ══ 1. CANDIDATE PROFILE ══ -->
      <div class="section">
        <div class="sec-title">1 · Candidate Profile</div>
        <div class="sec-body">
          <div class="info-grid">
            ${parsed.totalExperience ? `<div class="info-item"><label>Total Experience</label><span>${parsed.totalExperience}</span></div>` : ''}
            ${parsed.currentRole || parsed.currentTitle ? `<div class="info-item"><label>Current / Last Role</label><span>${parsed.currentRole || parsed.currentTitle}</span></div>` : ''}
            ${parsed.education?.length ? `<div class="info-item"><label>Education</label><span>${parsed.education.map(e => e.degree || e.institution || e).filter(Boolean).join(', ')}</span></div>` : ''}
            ${fullResume.matchScore > 0 ? `<div class="info-item"><label>Resume Match Score</label><span style="color:${scoreColor(fullResume.matchScore)}">${fullResume.matchScore}%</span></div>` : ''}
          </div>
          ${parsed.skills?.length ? `<div style="margin-top:12px"><label style="display:block;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#b5b1b2;margin-bottom:6px">Skills</label>
            <div style="display:flex;flex-wrap:wrap;gap:6px">${(parsed.skills||[]).slice(0,20).map(sk=>`<span style="background:#efeeea;color:#40505C;padding:3px 10px;border-radius:6px;font-size:12px;font-weight:500">${sk}</span>`).join('')}</div>
          </div>` : ''}
        </div>
      </div>

      <!-- ══ 2. HIRING JOURNEY — TIMELINE ══ -->
      <div class="section">
        <div class="sec-title">2 · Hiring Journey</div>
        <div class="sec-body">
          <div class="timeline">
            ${scoreRows.map(row => `
              <div class="tl-row">
                <div class="tl-dot" style="background:${scoreColor(row.score)}"></div>
                <div style="flex:1">
                  <div style="display:flex;justify-content:space-between;align-items:center">
                    <span style="font-weight:700;font-size:13px">${row.label}</span>
                    <div style="display:flex;align-items:center;gap:8px">
                      <span style="font-size:15px;font-weight:900;color:${scoreColor(row.score)}">${Math.round(row.score)}%</span>
                      ${statusBadge(row.status)}
                    </div>
                  </div>
                  ${row.completedAt ? `<div style="font-size:11px;color:#b5b1b2;margin-top:2px">${new Date(row.completedAt).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</div>` : ''}
                  ${row.earned != null ? `<div style="font-size:11px;color:#667683;margin-top:2px">Points: ${row.earned} / ${row.total}</div>` : ''}
                  <div class="bar-wrap" style="max-width:220px;margin-top:5px"><div class="bar" style="width:${Math.min(100,row.score||0)}%;background:${scoreColor(row.score)}"></div></div>
                </div>
              </div>
            `).join('')}
            ${scoreRows.length === 0 ? '<div style="color:#b5b1b2;font-size:13px">No stages completed yet.</div>' : ''}
          </div>
        </div>
      </div>

      <!-- ══ 3. ASSESSMENT SCORECARD ══ -->
      ${scoreRows.length ? `
      <div class="section">
        <div class="sec-title">3 · Assessment Scorecard</div>
        <div class="sec-body">
          <table>
            <thead><tr>
              <th>Round / Stage</th>
              <th style="text-align:center">Score</th>
              <th style="text-align:center">Status</th>
              <th style="text-align:center">Integrity</th>
            </tr></thead>
            <tbody>
            ${scoreRows.map((row, i) => `
              <tr style="background:${i%2===0?'#fff':'#F5F8FA'}">
                <td style="font-weight:600">${row.label}</td>
                <td style="text-align:center">
                  <span style="font-size:16px;font-weight:900;color:${scoreColor(row.score)}">${Math.round(row.score)}%</span>
                  ${row.earned != null ? `<div style="font-size:10px;color:#b5b1b2">${row.earned}/${row.total} pts</div>` : ''}
                  <div class="bar-wrap" style="margin-top:3px"><div class="bar" style="width:${Math.min(100,row.score||0)}%;background:${scoreColor(row.score)}"></div></div>
                </td>
                <td style="text-align:center">${statusBadge(row.status)}</td>
                <td style="text-align:center;font-size:12px">
                  ${row.proctor != null ? `<div>Proctoring: <strong>${row.proctor}%</strong></div>` : ''}
                  ${row.integrity != null ? `<div>Integrity: <strong>${row.integrity}%</strong></div>` : ''}
                  ${row.violations != null ? `<div style="color:#b5b1b2">${row.violations} violation${row.violations!==1?'s':''}</div>` : ''}
                  ${riskBadge(row.risk)}
                </td>
              </tr>
            `).join('')}
            ${overallAvg != null ? `
            <tr style="background:#D9F1F2;border-top:2px solid #fbd7cc">
              <td style="font-weight:800;font-size:14px">Overall Average</td>
              <td style="text-align:center;font-size:22px;font-weight:900;color:#0B7A82">${overallAvg}%</td>
              <td colspan="2"></td>
            </tr>` : ''}
            </tbody>
          </table>
        </div>
      </div>` : ''}

      <!-- ══ 4. DETAILED INTERVIEW REPORTS ══ -->
      ${reports.length ? `
      <div class="section">
        <div class="sec-title">4 · Detailed Interview Reports</div>
        <div class="sec-body" style="padding:0">
        ${reports.map((rep, ri) => `
          <div style="padding:18px 16px;${ri>0?'border-top:2px solid #efeeea':''}">
            <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:12px">
              <div>
                <div style="font-size:15px;font-weight:800;color:#1E2A33">${ROUND_LABEL[rep.round]||rep.round}${rep.stageLabel ? ` — ${rep.stageLabel}` : ''}</div>
                <div style="font-size:11px;color:#b5b1b2;margin-top:2px">${rep.jobTitle||''} · ${new Date(rep.completedAt).toLocaleString()}</div>
              </div>
              <div style="text-align:right">
                <div style="font-size:28px;font-weight:900;color:${scoreColor(rep.overallScore||0)};line-height:1">${rep.overallScore ?? '—'}<span style="font-size:13px;color:#b5b1b2;font-weight:400">/100</span></div>
                <div style="margin-top:4px">${recoBadge(rep.recommendation)}</div>
              </div>
            </div>
            ${briefScoresHtml(rep)}
            ${!hasBriefScores(rep) && rep.axisScores?.length ? `
            <div style="margin-bottom:12px">
              <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#b5b1b2;margin-bottom:8px">Axis Scores</div>
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
              ${rep.axisScores.map(a=>`
                <div>
                  <div style="display:flex;justify-content:space-between;font-size:12px"><span style="font-weight:600">${a.axis}</span><strong style="color:${scoreColor(a.score||0)}">${a.score??'—'}</strong></div>
                  <div class="bar-wrap"><div class="bar" style="width:${Math.min(100,a.score||0)}%;background:${scoreColor(a.score||0)}"></div></div>
                  ${a.note?`<div style="font-size:11px;color:#667683;margin-top:2px">${a.note}</div>`:''}
                </div>`).join('')}
              </div>
            </div>` : ''}
            ${rep.selectionNarrative ? `<div style="margin-bottom:10px"><div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#b5b1b2;margin-bottom:4px">Summary</div><p class="narrative" style="margin:0">${rep.selectionNarrative}</p></div>` : ''}
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px">
              ${rep.strengths?.length ? `<div><div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#1D7A46;margin-bottom:4px">Strengths</div>${rep.strengths.map(s=>`<div style="font-size:12px;margin-bottom:3px">✓ ${s}</div>`).join('')}</div>` : ''}
              ${rep.concerns?.length  ? `<div><div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#d0021b;margin-bottom:4px">Concerns</div>${rep.concerns.map(c=>`<div style="font-size:12px;margin-bottom:3px">⚠ ${c}</div>`).join('')}</div>` : ''}
            </div>
            ${(rep.cost?.usd > 0 || rep.cost?.tokens > 0) ? `
            <div style="display:flex;gap:20px;padding:10px 14px;background:#F5F8FA;border-radius:8px;margin-top:4px">
              <div style="font-size:11px;color:#b5b1b2;font-weight:700;text-transform:uppercase;letter-spacing:.05em;align-self:center">Interview Cost</div>
              ${rep.cost?.usd>0?`<div><div style="font-size:10px;color:#b5b1b2">USD</div><div style="font-size:16px;font-weight:800;color:#1E2A33">$${Number(rep.cost.usd).toFixed(4)}</div></div>`:''}
              ${rep.cost?.tokens>0?`<div><div style="font-size:10px;color:#b5b1b2">Tokens</div><div style="font-size:16px;font-weight:800;color:#1E2A33">${Number(rep.cost.tokens).toLocaleString()}</div></div>`:''}
            </div>` : ''}
          </div>
        `).join('')}
        </div>
      </div>` : ''}

      <!-- ══ 5. TOTAL COST INCURRED ══ -->
      ${costsData?.totalUsd > 0 || costsData?.totalTokens > 0 ? `
      <div class="section">
        <div class="sec-title">5 · Total Cost Incurred</div>
        <div class="sec-body">
          <div style="display:flex;gap:40px;align-items:center;margin-bottom:18px">
            <div>
              <div style="font-size:11px;color:#b5b1b2;font-weight:700;text-transform:uppercase;letter-spacing:.06em">Total AI Cost (USD)</div>
              <div style="font-size:34px;font-weight:900;color:#0B7A82">$${(costsData.totalUsd||0).toFixed(4)}</div>
            </div>
            <div>
              <div style="font-size:11px;color:#b5b1b2;font-weight:700;text-transform:uppercase;letter-spacing:.06em">Total Tokens Used</div>
              <div style="font-size:34px;font-weight:900;color:#1E2A33">${(costsData.totalTokens||0).toLocaleString()}</div>
            </div>
          </div>
          ${costsData.byStage?.filter(s=>s.usdCost>0||s.tokens>0).length ? `
          <table>
            <thead><tr>
              <th>Stage / Activity</th>
              <th style="text-align:right">Cost (USD)</th>
              <th style="text-align:right">Tokens</th>
            </tr></thead>
            <tbody>
            ${costsData.byStage.filter(s=>s.usdCost>0||s.tokens>0).map((s,i)=>`
              <tr style="background:${i%2===0?'#fff':'#F5F8FA'}">
                <td style="font-weight:500">${s.stage}</td>
                <td style="text-align:right;font-weight:700">$${(s.usdCost||0).toFixed(4)}</td>
                <td style="text-align:right;color:#40505C">${(s.tokens||0).toLocaleString()}</td>
              </tr>`).join('')}
            <tr style="background:#D9F1F2;border-top:2px solid #fbd7cc">
              <td style="font-weight:800">Total</td>
              <td style="text-align:right;font-weight:900;color:#0B7A82">$${(costsData.totalUsd||0).toFixed(4)}</td>
              <td style="text-align:right;font-weight:900">${(costsData.totalTokens||0).toLocaleString()}</td>
            </tr>
            </tbody>
          </table>` : ''}
        </div>
      </div>` : ''}

      <script>window.onload=()=>window.print()</script>
      </body></html>`;

      const w = window.open('', '_blank');
      if (!w) { toast.error('Pop-up blocked — allow pop-ups for this site'); return; }
      w.document.write(html);
      w.document.close();
    } catch (e) {
      toast.error('Failed to load report');
    } finally {
      setDownloadingReport(null);
    }
  };
  const [reprocessingResume, setReprocessingResume] = useState(null);
  const [showInterviewDetails, setShowInterviewDetails] = useState(false);
  const [selectedInterviewData, setSelectedInterviewData] = useState({ resume: null, type: null });
  const [showPsychoSessionModal, setShowPsychoSessionModal] = useState(false);
  const [psychoSessionData, setPsychoSessionData] = useState(null);
  const [psychoSessionLoading, setPsychoSessionLoading] = useState(false);
  const [psychoSessionError, setPsychoSessionError] = useState(null);
  const [invitingResume, setInvitingResume] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Clear inviting state when resumes data changes (indicating invite was processed)
  useEffect(() => {
    if (invitingResume) {
      setInvitingResume(null);
    }
  }, [resumes]);

  // Handler for viewing interview details
  const handleViewInterviewDetails = (resume, interviewType) => {
    setSelectedInterviewData({ resume, type: interviewType });
    setShowInterviewDetails(true);
  };

  // Check if interview is completed and has data to show details
  const hasInterviewDetails = (resume, type) => {
    switch (type) {
      case 'preScreening':
        // For conversational interviews, check if the interview is completed
        return resume.inviteStatus?.preScreening === 'completed' && resume.preScreeningScore > 0;
      case 'psychoanalytical':
        return resume.psychoanalyticalStatus === 'completed' && resume.psychoanalyticalResult;
      case 'finalInterview':
        return resume.finalInterviewStatus === 'completed' && resume.finalInterviewResult;
      default:
        return false;
    }
  };

  // Define columns based on screenType
  const getColumns = () => {
    const baseColumns = [
      {
        key: 'fileName',
        header: 'Candidate Info',
        render: (value, resume) => (
          <div>
            <button 
              onClick={() => {
                setSelectedResume(resume);
                setShowResumeViewer(true);
              }}
              className="text-left hover:text-orange-600"
            >
              <div className="font-medium">
                {resume.candidateName || resume.analysis?.parsedData?.name || 'Name not extracted'}
              </div>
              <div className="text-sm text-gray-600">{value}</div>
              <div className="text-sm text-gray-500">
                {resume.analysis?.parsedData?.email || resume.email || 'Email not extracted'}
              </div>
            </button>
          </div>
        )
      }
    ];

    // Application fee, straight off the candidate record. The payments module
    // flips it to paid when the money lands, so this is read-only here — a
    // human wanting to change it goes through payments, not this table.
    const paymentColumn = {
      key: 'payment',
      header: 'Payment',
      render: (value, resume) => {
        const status = resume.payment?.status || 'unpaid';
        const look = {
          paid:   'bg-green-100 text-green-800',
          waived: 'bg-gray-100 text-gray-600',
          unpaid: 'bg-amber-100 text-amber-800',
        }[status] || 'bg-amber-100 text-amber-800';
        const label = { paid: 'Paid', waived: 'Waived', unpaid: 'Unpaid' }[status] || status;
        return (
          <span className={`px-2 py-1 text-xs rounded ${look}`}>
            {label}
          </span>
        );
      },
    };

    // Add columns based on screenType
    switch(screenType) {
      case 'resumeScreening':
        return [
          ...baseColumns,
          paymentColumn,
          {
            key: 'processingStatus',
            header: 'Processing',
            render: (value, resume) => {
              const getStatusColor = (status) => {
                switch (status) {
                  case 'completed':   return 'bg-green-100 text-green-800';
                  case 'processing':  return 'bg-blue-100 text-blue-800';
                  case 'failed':      return 'bg-red-100 text-red-800';
                  case 'rejected':    return 'bg-red-100 text-red-800';
                  default:            return 'bg-gray-100 text-gray-800';
                }
              };

              const getStatusText = (status) => {
                switch (status) {
                  case 'completed':  return 'Completed';
                  case 'processing': return 'Processing';
                  case 'failed':     return 'Failed';
                  case 'uploaded':   return 'Queued';
                  case 'rejected':   return 'Rejected';
                  default:           return 'Unknown';
                }
              };

              // The CV could not be read: say so, with the reason, instead of
              // 'Failed' or 'Completed' (a score made without the CV is none).
              if (isCvUnread(resume)) {
                return (
                  <div>
                    <span className="px-2 py-1 text-xs rounded bg-amber-100 text-amber-800">{CV_UNREAD_LABEL}</span>
                    {resume.cvUnreadReason && (
                      <div className="text-xs text-amber-700 mt-1" title={resume.cvUnreadReason}>
                        {resume.cvUnreadReason.length > 60 ? `${resume.cvUnreadReason.substring(0, 60)}...` : resume.cvUnreadReason}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <div>
                  <span className={`px-2 py-1 text-xs rounded ${getStatusColor(value)}`}>
                    {getStatusText(value)}
                  </span>
                  {resume.rejectionReason && resume.processingStatus !== 'completed' && (
                    <div className="text-xs text-red-600 mt-1" title={resume.rejectionReason}>
                      {resume.rejectionReason.length > 50
                        ? `${resume.rejectionReason.substring(0, 50)}...`
                        : resume.rejectionReason}
                    </div>
                  )}
                </div>
              );
            }
          },
          {
            key: 'aiScore',
            header: 'ATS Score',
            render: (value, resume) => {
              if (isCvUnread(resume)) {
                return <span className="px-2 py-1 text-xs rounded bg-amber-50 text-amber-800" title={CV_UNREAD_LABEL}>No score</span>;
              }
              // Use the accurate score from analysis.scores.atsScore, fallback to aiScore if not available
              const accurateScore = resume.analysis?.scores?.atsScore !== undefined ? 
                resume.analysis.scores.atsScore : 
                resume.aiScore;
              
              // Show score if resume is completed OR if accurate score exists
              if (resume.processingStatus === 'completed' || accurateScore !== undefined) {
                return (
                  <span className="px-2 py-1 text-sm rounded bg-blue-100 text-blue-800">
                    {accurateScore || 0}%
                  </span>
                );
              } else {
                return (
                  <span className="px-2 py-1 text-sm rounded bg-gray-100 text-gray-500">
                    Processing...
                  </span>
                );
              }
            }
          },
          {
            key: 'matchScore',
            header: 'Match Score',
            render: (value, resume) => {
              if (isCvUnread(resume)) {
                return <span className="px-2 py-1 text-xs rounded bg-amber-50 text-amber-800" title={CV_UNREAD_LABEL}>No score</span>;
              }
              // Use the accurate score from analysis.scores.jdMatch, fallback to matchScore if not available
              const accurateScore = resume.analysis?.scores?.jdMatch !== undefined ? 
                resume.analysis.scores.jdMatch : 
                resume.matchScore;
              
              // Show score if resume is completed OR if accurate score exists
              if (resume.processingStatus === 'completed' || accurateScore !== undefined) {
                return (
                  <span className="px-2 py-1 text-sm rounded bg-green-100 text-green-800">
                    {accurateScore || 0}/100
                  </span>
                );
              } else {
                return (
                  <span className="px-2 py-1 text-sm rounded bg-gray-100 text-gray-500">
                    Processing...
                  </span>
                );
              }
            }
          },
          {
            key: 'resumeScreeningStatus',
            header: 'Status',
            type: 'status',
            // review_later: under the CV bar, for a person to look at (HIR-HR10).
            options: ['shortlisted', 'review_later', 'rejected'],
            render: (value, resume) => {
              // Use the accurate shortlisting status from analysis.shortlisted, fallback to resumeScreeningStatus if not available
              const accurateStatus = resume.analysis?.shortlisted !== undefined ? 
                (resume.analysis.shortlisted ? 'shortlisted' : 'rejected') :
                resume.resumeScreeningStatus;
              
              const getStatusColor = (status) => {
                switch (status) {
                  case 'shortlisted':
                    return 'bg-green-100 text-green-800';
                  case 'rejected':
                    return 'bg-red-100 text-red-800';
                  default:
                    return 'bg-gray-100 text-gray-800';
                }
              };
              
              const getStatusText = (status) => {
                switch (status) {
                  case 'shortlisted':
                    return 'Shortlisted';
                  case 'rejected':
                    return 'Rejected';
                  default:
                    return 'Unknown';
                }
              };
              
              return (
                <div>
                  <span className={`px-2 py-1 text-xs rounded ${getStatusColor(accurateStatus)}`}>
                    {getStatusText(accurateStatus)}
                  </span>
                  {resume.rejectionReason && resume.processingStatus !== 'completed' && (
                    <div className="text-xs text-red-600 mt-1" title={resume.rejectionReason}>
                      {resume.rejectionReason.length > 50
                        ? `${resume.rejectionReason.substring(0, 50)}...`
                        : resume.rejectionReason}
                    </div>
                  )}
                </div>
              );
            }
          },
          {
            key: 'actions',
            header: 'Actions',
            render: (_, resume) => (
              <div className="flex items-center justify-center gap-2">
                {/* Only show Reprocess for resumeScreening, not preScreening */}
                {screenType === 'resumeScreening' && (
                  <button
                    onClick={() => handleReprocessResume(resume)}
                    disabled={reprocessingResume === resume._id || resume.processingStatus === 'processing' || resume.processingStatus === 'uploaded'}
                    className="text-blue-600 hover:text-blue-800 disabled:opacity-50 flex items-center gap-1"
                    title={resume.processingStatus === 'processing' || resume.processingStatus === 'uploaded' ? 'Resume is being processed' : 'Reprocess Resume'}
                  >
                    {reprocessingResume === resume._id || resume.processingStatus === 'processing' || resume.processingStatus === 'uploaded' ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <RotateCcw className="w-4 h-4" />
                    )}
                    {reprocessingResume === resume._id ? 'Processing...' : 
                     resume.processingStatus === 'processing' || resume.processingStatus === 'uploaded' ? 'Processing...' : 'Reprocess'}
                  </button>
                )}
              </div>
            )
          }
        ];

      case 'preScreening':
        return [
          ...baseColumns,
          {
            key: 'resumeScreeningStatus',
            header: 'Resume Status',
            render: (value) => (
              <span className={`px-2 py-1 text-sm rounded ${
                value === 'shortlisted' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {value}
              </span>
            )
          },
          {
            key: 'preScreeningStatus',
            header: 'Pre-Screening Status',
            type: 'status',
            options: ['shortlisted', 'rejected']
          },
          {
            key: 'preScreeningScore',
            header: 'Test Score',
            render: (value, resume) => {
              return resume.inviteStatus?.preScreening === 'completed' ? 
                renderScoreCell(value, resume, 'preScreening') :
                <span className="text-gray-400">
                  {resume.inviteStatus?.preScreening === 'sent' ? 'Pending' : 'Not attempted'}
                </span>;
            }
          },
          {
            key: 'interviewDetails',
            header: 'Interview Details',
            render: (_, resume) => (
              <div className="text-center">
                {hasInterviewDetails(resume, 'preScreening') ? (
                  <button
                    onClick={() => handleViewInterviewDetails(resume, 'conversational')}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View Details
                  </button>
                ) : (
                  <span className="text-gray-400 text-sm">No details</span>
                )}
              </div>
            )
          },
          {
            key: 'inviteStatus',
            header: 'Invite Status',
            render: (value) => (
              <span className={`px-2 py-1 text-sm rounded ${
                value?.preScreening === 'completed' 
                  ? 'bg-green-100 text-green-800' 
                  : value?.preScreening === 'sent'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
              }`}>
                {value?.preScreening === 'completed' 
                  ? 'Completed' 
                  : value?.preScreening === 'sent'
                    ? 'Sent'
                    : 'Pending'}
              </span>
            )
          },
          {
            key: 'inviteLink',
            header: 'Invite Link',
            render: (_, resume) => {
              const link = screenType === 'preScreening'
                ? `${config.baseUrl}/pre-screening-test/${id}/${resume._id}`
                : `${config.baseUrl}/psychoanalytical-test/${id}/${resume._id}`;

              return (
                <div 
                  onClick={() => {
                    try {
                      navigator.clipboard.writeText(link);
                      toast.success('Link copied to clipboard!');
    } catch (error) {
                      console.error('Failed to copy:', error);
                      toast.error('Failed to copy link');
                    }
                  }}
                  className="cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors group flex items-center"
                >
                  <span className="text-blue-600 text-sm truncate max-w-[200px]">{link}</span>
                  <span className="text-gray-400 text-xs ml-2 opacity-0 group-hover:opacity-100">
                    Click to copy
                  </span>
                </div>
              );
            }
          },
          // Removed Questions column for preScreening - all links call Interview API directly
          {
            key: 'selectionStatus',
            header: 'Selection',
            render: (_, resume) => (
              <SelectionStatusDropdown
                resumeId={resume._id}
                current={resume.preScreeningStatus}
                statusType="preScreening"
                onChanged={() => {}}
              />
            )
          },
          {
            key: 'actions',
            header: 'Actions',
            render: (_, resume) => (
              <div className="flex items-center gap-2">
                {/* Send/Resend Invite for preScreening */}
                {screenType === 'preScreening' && (
                  <>
                    {(!resume.inviteStatus?.preScreening || resume.inviteStatus.preScreening === 'pending') && (
                      <button
                        onClick={() => {
                          setInvitingResume(resume._id);
                          onInvite && onInvite(resume);
                        }}
                        disabled={loading || invitingResume === resume._id}
                        className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200 text-xs disabled:opacity-50 flex items-center gap-1 transition-colors duration-200"
                        title="Send Invite"
                      >
                        {invitingResume === resume._id ? (
                          <>
                            <RefreshCw className="w-3 h-3 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          'Send Invite'
                        )}
                      </button>
                    )}
                    {resume.inviteStatus?.preScreening === 'sent' && (
                      <button
                        onClick={() => {
                          setInvitingResume(resume._id);
                          onInvite && onInvite(resume);
                        }}
                        disabled={loading || invitingResume === resume._id}
                        className="px-3 py-1.5 bg-orange-100 text-orange-800 rounded-md hover:bg-orange-200 text-xs disabled:opacity-50 flex items-center gap-1 transition-colors duration-200"
                        title="Resend Invite"
                      >
                        {invitingResume === resume._id ? (
                          <>
                            <RefreshCw className="w-3 h-3 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          'Resend Invite'
                        )}
                      </button>
                    )}
                    {resume.inviteStatus?.preScreening === 'completed' && (
                      <button
                        onClick={async () => {
                          try {
                            await resumeApi.resetTestAttempt(resume._id, 'preScreening');
                            toast.success('Test attempt reset. Candidate can reattempt.');
                            if (typeof onPageChange === 'function') onPageChange(pagination?.page || 1);
                          } catch (error) {
                            toast.error('Failed to reset attempt');
                          }
                        }}
                        className="px-3 py-1.5 bg-yellow-100 text-yellow-800 rounded-md hover:bg-yellow-200 text-xs transition-colors duration-200"
                      >
                        Reset Attempt
                      </button>
                    )}
                  </>
                )}
                {/* Only show Reprocess for resumeScreening, not preScreening */}
                {screenType === 'resumeScreening' && (
                  <button
                    onClick={() => handleReprocessResume(resume)}
                    disabled={reprocessingResume === resume._id}
                    className="text-blue-600 hover:text-blue-800 disabled:opacity-50 flex items-center gap-1"
                    title="Reprocess Resume"
                  >
                    {reprocessingResume === resume._id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <RotateCcw className="w-4 h-4" />
                    )}
                    {reprocessingResume === resume._id ? 'Processing...' : 'Reprocess'}
                  </button>
                )}
              </div>
            )
          }
        ];

      case 'psychoanalytical':
        return [
          ...baseColumns,
          {
            key: 'preScreeningStatus',
            header: 'Pre-Screening Status',
            render: (value) => (
              <span className={`px-2 py-1 text-sm rounded ${
                value === 'shortlisted' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {value}
              </span>
            )
          },
          {
            key: 'psychoanalyticalStatus',
            header: 'Psychoanalytical Status',
            type: 'status',
            options: ['not_started', 'in_progress', 'completed', 'shortlisted', 'rejected']
          },
          {
            key: 'psychoanalyticalScore',
            header: 'Interview Score',
            render: (_, resume) => (
              <div className="text-center">
                {resume.psychoanalyticalResult?.score ? (
                  <span className={`px-2 py-1 text-sm rounded ${
                    resume.psychoanalyticalResult?.score >= jobDetails?.screeningConfig?.psychoanalytical?.passingScore
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {resume.psychoanalyticalResult?.score || 0}%
                  </span>
                ) : (
                  <span className="text-gray-400">Pending</span>
                )}
              </div>
            )
          },
   
          {
            key: 'inviteStatus',
            header: 'Invite Status',
            render: (value) => (
              <span className={`px-2 py-1 text-sm rounded ${
                value?.psychoanalytical === 'completed' 
                  ? 'bg-green-100 text-green-800' 
                  : value?.psychoanalytical === 'sent'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
              }`}>
                {value?.psychoanalytical === 'completed' 
                  ? 'Completed' 
                  : value?.psychoanalytical === 'sent'
                    ? 'Sent'
                    : 'Pending'}
              </span>
            )
          },
          {
            key: 'inviteLink',
            header: 'Invite Link',
            render: (_, resume) => {
              const link = `${config.baseUrl}/psychoanalytical-test/${id}/${resume._id}`;
              return (
                <div 
                  onClick={() => {
                    try {
                      navigator.clipboard.writeText(link);
                      toast.success('Link copied to clipboard!');
                    } catch (error) {
                      console.error('Failed to copy:', error);
                      toast.error('Failed to copy link');
                    }
                  }}
                  className="cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors group flex items-center"
                >
                  <span className="text-blue-600 text-sm truncate max-w-[200px]">{link}</span>
                  <span className="text-gray-400 text-xs ml-2 opacity-0 group-hover:opacity-100">
                    Click to copy
                  </span>
                </div>
              );
            }
          },
          {
            key: 'feedback',
            header: 'AI Feedback',
            render: (_, resume) => (
              <div>
                {resume.psychoanalyticalResult?.feedback ? (
                  <button 
                    onClick={() => onViewFeedback(resume)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    View Feedback
                  </button>
                ) : (
                  <span className="text-gray-400">No feedback yet</span>
                )}
              </div>
            )
          },
          {
            key: 'interviewDetails',
            header: 'Interview Details',
            render: (_, resume) => (
              <div className="text-center">
                {hasInterviewDetails(resume, 'psychoanalytical') ? (
                  <button
                    onClick={() => handleViewInterviewDetails(resume, 'psychoanalytical')}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View Details
                  </button>
                ) : (
                  <span className="text-gray-400 text-sm">No details</span>
                )}
              </div>
            )
          },
          {
            key: 'psychoSession',
            header: 'Session Details',
            render: (_, resume) => (
              <div className="text-center">
                {resume.psychoanalyticalStatus !== 'not_started' ? (
                  <button
                    onClick={() => handleViewPsychoSession(resume)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Session Details
                  </button>
                ) : (
                  <span className="text-gray-400 text-sm">No session</span>
                )}
              </div>
            )
          },
          {
            key: 'selectionStatus',
            header: 'Selection',
            render: (_, resume) => (
              <SelectionStatusDropdown
                resumeId={resume._id}
                current={resume.psychoanalyticalStatus}
                statusType="psychoanalytical"
                onChanged={() => {}}
              />
            )
          },
          {
            key: 'actions',
            header: 'Actions',
            render: (_, resume) => (
              <div className="flex items-center justify-end gap-2">
                {screenType === 'psychoanalytical' && (
                  <>
                    {(!resume.inviteStatus?.psychoanalytical || resume.inviteStatus.psychoanalytical === 'pending') && (
                      <button
                        onClick={() => {
                          setInvitingResume(resume._id);
                          onInvite && onInvite(resume);
                        }}
                        disabled={loading || invitingResume === resume._id}
                        className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200 text-xs disabled:opacity-50 flex items-center gap-1 transition-colors duration-200"
                        title="Send Invite"
                      >
                        {invitingResume === resume._id ? (
                          <>
                            <RefreshCw className="w-3 h-3 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          'Send Invite'
                        )}
                      </button>
                    )}
                    {resume.inviteStatus?.psychoanalytical === 'sent' && (
                      <button
                        onClick={() => {
                          setInvitingResume(resume._id);
                          onInvite && onInvite(resume);
                        }}
                        disabled={loading || invitingResume === resume._id}
                        className="px-3 py-1.5 bg-orange-100 text-orange-800 rounded-md hover:bg-orange-200 text-xs disabled:opacity-50 flex items-center gap-1 transition-colors duration-200"
                        title="Resend Invite"
                      >
                        {invitingResume === resume._id ? (
                          <>
                            <RefreshCw className="w-3 h-3 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          'Resend Invite'
                        )}
                      </button>
                    )}
                    {resume.inviteStatus?.psychoanalytical === 'completed' && (
                      <button
                        onClick={async () => {
                          try {
                            await resumeApi.resetTestAttempt(resume._id, 'psychoanalytical');
                            toast.success('Test attempt reset. Candidate can reattempt.');
                            if (typeof onPageChange === 'function') onPageChange(pagination?.page || 1);
                          } catch (error) {
                            toast.error('Failed to reset attempt');
                          }
                        }}
                        className="px-3 py-1.5 bg-yellow-100 text-yellow-800 rounded-md hover:bg-yellow-200 text-xs transition-colors duration-200"
                      >
                        Reset Attempt
                      </button>
                    )}
                  </>
                )}
              </div>
            )
          }
        ];

      case 'finalInterview':
        return [
          ...baseColumns,
          {
            key: 'psychoanalyticalStatus',
            header: 'Psychoanalytical Status',
            render: (value) => (
              <span className={`px-2 py-1 text-sm rounded ${
                value === 'shortlisted' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {value}
              </span>
            )
          },
          {
            key: 'inviteStatus',
            header: 'Invite Status',
            render: (value) => (
              <span className={`px-2 py-1 text-sm rounded ${
                value?.finalInterview === 'completed' 
                  ? 'bg-green-100 text-green-800' 
                  : value?.finalInterview === 'sent'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
              }`}>
                {value?.finalInterview === 'completed' 
                  ? 'Completed' 
                  : value?.finalInterview === 'sent'
                    ? 'Sent'
                    : 'Pending'}
              </span>
            )
          },
          {
            key: 'finalInterviewStatus',
            header: 'Interview Status',
            type: 'status',
            options: ['not_started', 'in_progress', 'completed', 'shortlisted', 'rejected']
          },
          {
            key: 'finalInterviewScore',
            header: 'Interview Score',
            render: (value, resume) => renderScoreCell(value, resume, 'finalInterview')
          },
          {
            key: 'inviteLink',
            header: 'Invite Link',
            render: (_, resume) => {
              const link = `${config.baseUrl}/final-interview/${id}/${resume._id}`;
              return (
                <div 
                  onClick={() => {
                    try {
                      navigator.clipboard.writeText(link);
                      toast.success('Link copied to clipboard!');
                    } catch (error) {
                      console.error('Failed to copy:', error);
                      toast.error('Failed to copy link');
                    }
                  }}
                  className="cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors group flex items-center"
                >
                  <span className="text-blue-600 text-sm truncate max-w-[200px]">{link}</span>
                  <span className="text-gray-400 text-xs ml-2 opacity-0 group-hover:opacity-100">
                    Click to copy
                  </span>
                </div>
              );
            }
          },
          {
            key: 'feedback',
            header: 'AI Feedback',
            render: (_, resume) => (
              <div>
                {resume.finalInterviewResult?.detailedAnalysis ? (
                  <button 
                    onClick={() => onViewFeedback(resume)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    View Feedback
                  </button>
                ) : (
                  <span className="text-gray-400">No feedback yet</span>
                )}
              </div>
            )
          },
          {
            key: 'interviewDetails',
            header: 'Interview Details',
            render: (_, resume) => (
              <div className="text-center">
                {hasInterviewDetails(resume, 'finalInterview') ? (
                  <button
                    onClick={() => handleViewInterviewDetails(resume, 'finalInterview')}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View Details
                  </button>
                ) : (
                  <span className="text-gray-400 text-sm">No details</span>
                )}
              </div>
            )
          },
          {
            key: 'hrFeedback',
            header: 'HR Feedback',
            render: (_, resume) => (
              <div className="min-w-[200px]">
                {resume.finalInterviewResult?.hrFeedback?.feedback ? (
                  <div className="space-y-2">
                    <div className="text-sm text-gray-700 bg-gray-50 p-2 rounded border max-w-xs">
                      {resume.finalInterviewResult.hrFeedback.feedback}
                    </div>
                    <div className="text-xs text-gray-500">
                      Added: {new Date(resume.finalInterviewResult.hrFeedback.addedAt).toLocaleDateString()}
                    </div>
                    <button
                      onClick={() => handleEditHRFeedback(resume)}
                      className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                    >
                      Edit
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => handleEditHRFeedback(resume)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Add Feedback
                  </button>
                )}
              </div>
            )
          },
          {
            key: 'selectionStatus',
            header: 'Selection',
            render: (_, resume) => (
              <SelectionStatusDropdown
                resumeId={resume._id}
                current={resume.finalInterviewStatus}
                statusType="finalInterview"
                onChanged={() => {}}
              />
            )
          },
          {
            key: 'actions',
            header: 'Actions',
            render: (_, resume) => (
              <div className="flex items-center justify-end gap-2">
                {screenType === 'finalInterview' && (
                  <>
                    {(!resume.inviteStatus?.finalInterview || resume.inviteStatus.finalInterview === 'pending') && (
                      <button
                        onClick={() => {
                          setInvitingResume(resume._id);
                          onInvite && onInvite(resume);
                        }}
                        disabled={loading || invitingResume === resume._id}
                        className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200 text-xs disabled:opacity-50 flex items-center gap-1 transition-colors duration-200"
                        title="Send Invite"
                      >
                        {invitingResume === resume._id ? (
                          <>
                            <RefreshCw className="w-3 h-3 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          'Send Invite'
                        )}
                      </button>
                    )}
                    {resume.inviteStatus?.finalInterview === 'sent' && (
                      <button
                        onClick={() => {
                          setInvitingResume(resume._id);
                          onInvite && onInvite(resume);
                        }}
                        disabled={loading || invitingResume === resume._id}
                        className="px-3 py-1.5 bg-orange-100 text-orange-800 rounded-md hover:bg-orange-200 text-xs disabled:opacity-50 flex items-center gap-1 transition-colors duration-200"
                        title="Resend Invite"
                      >
                        {invitingResume === resume._id ? (
                          <>
                            <RefreshCw className="w-3 h-3 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          'Resend Invite'
                        )}
                      </button>
                    )}
                    {resume.inviteStatus?.finalInterview === 'completed' && (
                      <button
                        onClick={async () => {
                          try {
                            await resumeApi.resetTestAttempt(resume._id, 'finalInterview');
                            toast.success('Test attempt reset. Candidate can reattempt.');
                            if (typeof onPageChange === 'function') onPageChange(pagination?.page || 1);
                          } catch (error) {
                            toast.error('Failed to reset attempt');
                          }
                        }}
                        className="px-3 py-1.5 bg-yellow-100 text-yellow-800 rounded-md hover:bg-yellow-200 text-xs transition-colors duration-200"
                      >
                        Reset Attempt
                      </button>
                    )}
                  </>
                )}
              </div>
            )
          }
        ];

      case 'offer-letter':
        return [
          ...baseColumns,
          {
            key: 'overallScore',
            header: 'Overall Score',
            render: (_, resume) => {
              const score = computeOverallScore(resume);
              if (score == null) return <span className="px-2 py-1 text-sm rounded bg-gray-100 text-gray-400">—</span>;
              const color = score >= 75 ? 'bg-green-100 text-green-800' : score >= 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-700';
              return (
                <div className="text-center">
                  <span className={`px-2.5 py-1 text-sm font-bold rounded ${color}`}>{score}%</span>
                  <div className="mt-1 h-1.5 bg-gray-100 rounded-full w-16 mx-auto overflow-hidden">
                    <div className={`h-full rounded-full ${score>=75?'bg-green-500':score>=50?'bg-yellow-400':'bg-red-500'}`} style={{width:`${score}%`}} />
                  </div>
                  {(resume.pipelineStageResults?.length > 0 || resume.finalInterviewScore > 0) && (
                    <div className="text-[10px] text-gray-400 mt-0.5">
                      {[
                        resume.aiScore > 0 && `ATS:${resume.aiScore}%`,
                        resume.preScreeningScore > 0 && `PS:${resume.preScreeningScore}%`,
                        resume.psychoanalyticalScore > 0 && `PA:${resume.psychoanalyticalScore}%`,
                        resume.finalInterviewScore > 0 && `FI:${resume.finalInterviewScore}%`,
                        ...getPipelineStages(resume).filter(p=>p.score!=null).map(p=>`${(p.label||'').split(' ')[0]}:${p.score}%`),
                      ].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
              );
            }
          },
          {
            key: 'assessments',
            header: 'Assessments Attended',
            render: (_, resume) => {
              const rows = [];

              // Legacy rounds — only show if actually attempted
              [
                { label: 'Pre-Screening',    score: resume.preScreeningScore,     status: resume.preScreeningStatus },
                { label: 'Psychoanalytical', score: resume.psychoanalyticalScore, status: resume.psychoanalyticalStatus },
                { label: 'Final Interview',  score: resume.finalInterviewScore,   status: resume.finalInterviewStatus },
                { label: 'Coding Test',      score: resume.codingScore,           status: resume.codingStatus },
              ].forEach(r => {
                if ((r.score != null && r.score > 0) || (r.status && r.status !== 'not_started' && r.status !== null))
                  rows.push(r);
              });

              // Pipeline stage results — only completed, labelled, non-legacy stages
              getPipelineStages(resume).forEach(p =>
                rows.push({ label: p.label, score: p.score, status: p.status })
              );

              if (rows.length === 0)
                return <span className="text-xs text-gray-400 italic">No assessments yet</span>;

              const sCls = (s) =>
                s === 'shortlisted' ? 'bg-emerald-100 text-emerald-700' :
                s === 'rejected'    ? 'bg-red-100 text-red-600' :
                s === 'completed'   ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500';
              const numCls = (n) =>
                n >= 75 ? 'text-emerald-700 font-bold' :
                n >= 50 ? 'text-amber-600 font-bold' : 'text-red-600 font-bold';

              return (
                <div className="space-y-1.5 min-w-[210px]">
                  {rows.map((row, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-xs bg-gray-50 rounded-lg px-2.5 py-1.5">
                      <span className="text-gray-700 font-medium truncate max-w-[115px]" title={row.label}>{row.label}</span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {row.score != null
                          ? <span className={numCls(row.score)}>{Math.round(row.score)}%</span>
                          : <span className="text-gray-400 text-[10px]">no score</span>
                        }
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold capitalize ${sCls(row.status)}`}>
                          {(row.status || 'pending').replace(/_/g, ' ')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              );
            }
          },
          {
            key: 'offerLetterStatus',
            header: 'Offer Status',
            render: (value) => {
              const label = (value || 'not_generated')
                .replace(/_/g, ' ')
                .replace(/\b\w/g, c => c.toUpperCase());
              return (
                <span className={`px-2 py-1 text-sm rounded font-medium ${
                  value === 'generated' ? 'bg-blue-100 text-blue-800' :
                  value === 'sent'      ? 'bg-green-100 text-green-800' :
                  value === 'accepted'  ? 'bg-purple-100 text-purple-800' :
                  value === 'rejected' || value === 'declined' ? 'bg-red-100 text-red-800' :
                  value === 'lapsed'    ? 'bg-amber-100 text-amber-800' :
                  'bg-gray-100 text-gray-500'
                }`}>
                  {label}
                </span>
              );
            }
          },
          {
            key: 'selectionStatus',
            header: 'Selection',
            render: (_, resume) => {
              // Determine the last pipeline stage and its result for this candidate.
              // Custom pipeline: use pipelineStageResults for the last enabled stage.
              // Legacy pipeline: fall back to finalInterviewStatus.
              const enabledStages = (pipelineStages || []).filter(s => s.enabled !== false);
              const lastStage = enabledStages[enabledStages.length - 1];
              let currentStatus = resume.finalInterviewStatus;
              let statusType    = 'finalInterview';

              if (lastStage && !lastStage.legacy) {
                // Custom stage — look up result in pipelineStageResults
                const psr = (resume.pipelineStageResults || [])
                  .find(p => p.stageId === lastStage.stageId);
                currentStatus = psr?.status || null;
                statusType    = 'pipelineStage_' + lastStage.stageId;
              }

              return (
                <SelectionStatusDropdown
                  resumeId={resume._id}
                  current={currentStatus}
                  statusType={lastStage?.legacy ? 'finalInterview' : 'finalInterview'}
                  onChanged={(newStatus) => {
                    if (onStatusChange) onStatusChange(resume._id, 'finalInterview', newStatus, true);
                  }}
                />
              );
            }
          },
          {
            key: 'actions',
            header: 'Actions',
            render: (_, resume) => (
              <div className="flex items-center justify-end gap-1.5">
                {/* Generate (first time) */}
                {(!resume.offerLetterStatus || resume.offerLetterStatus === 'not_generated') && (
                  <button
                    onClick={() => onGenerateOffer(resume._id)}
                    disabled={generatingOffer === resume._id}
                    className="px-2.5 py-1.5 text-xs font-semibold bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                  >
                    {generatingOffer === resume._id ? 'Generating…' : 'Generate'}
                  </button>
                )}

                {/* Download Report — always shown in offer-letter view */}
                <button
                  onClick={() => handleDownloadReport(resume)}
                  disabled={downloadingReport === resume._id}
                  className="p-1.5 rounded-lg hover:bg-indigo-50 text-gray-500 hover:text-indigo-600 disabled:opacity-50"
                  title="Download Interview Report"
                >
                  {downloadingReport === resume._id
                    ? <RefreshCw className="w-4 h-4 animate-spin" />
                    : <Download className="w-4 h-4" />}
                </button>

                {/* View, Edit, Regenerate, Send — when offer exists */}
                {(resume.offerLetterStatus === 'generated' || resume.offerLetterStatus === 'sent') && resume.offerLetter && (
                  <div className="flex items-center gap-1.5">
                    {/* View */}
                    <button
                      onClick={() => {
                        setSelectedOfferLetter({ content: resume.offerLetter.content, company: resume.offerLetter.company || null });
                        setShowOfferLetter(true);
                      }}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                      title="View Offer Letter"
                    >
                      <Eye className="w-4 h-4" />
                    </button>

                    {/* Edit */}
                    <button
                      onClick={() => setEditOfferState({ resumeId: resume._id, content: resume.offerLetter.content })}
                      className="p-1.5 rounded-lg hover:bg-orange-50 text-gray-500 hover:text-orange-500"
                      title="Edit Offer Letter"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>

                    {/* Regenerate */}
                    <button
                      onClick={() => onGenerateOffer(resume._id)}
                      disabled={generatingOffer === resume._id}
                      className="p-1.5 rounded-lg hover:bg-violet-50 text-gray-500 hover:text-violet-600 disabled:opacity-50"
                      title="Regenerate Offer Letter"
                    >
                      <RefreshCw className={`w-4 h-4 ${generatingOffer === resume._id ? 'animate-spin' : ''}`} />
                    </button>

                    {/* Send */}
                    <button
                      onClick={() => onSendOffer(resume._id)}
                      disabled={loading || resume.offerLetterStatus === 'sent'}
                      className="p-1.5 rounded-lg hover:bg-green-50 text-gray-500 hover:text-green-600 disabled:opacity-50"
                      title={resume.offerLetterStatus === 'sent' ? 'Already Sent' : 'Send Offer Letter'}
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            )
          }
        ];
      default:
        return [
          ...baseColumns,
          {
            key: 'finalInterviewScore',
            header: 'Interview Score',
            render: (value) => (
              <span className="px-2 py-1 text-sm rounded bg-blue-100 text-blue-800">
                {value || 0}
              </span>
            )
          },
          {
            key: 'finalInterviewStatus',
            header: 'Interview Status',
            render: (value) => (
              <span className={`px-2 py-1 text-sm rounded ${
                value === 'shortlisted' ? 'bg-green-100 text-green-800' :
                value === 'rejected' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {value?.charAt(0).toUpperCase() + value?.slice(1) || 'Pending'}
              </span>
            )
          },
          {
            key: 'offerLetterStatus',
            header: 'Offer Status',
            render: (value) => (
              <span className={`px-2 py-1 text-sm rounded ${
                value === 'generated' ? 'bg-blue-100 text-blue-800' :
                value === 'sent' ? 'bg-green-100 text-green-800' :
                value === 'accepted' ? 'bg-purple-100 text-purple-800' :
                value === 'rejected' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {value?.charAt(0).toUpperCase() + value?.slice(1) || 'Not Generated'}
              </span>
            )
          },
          {
            key: 'actions',
            header: 'Actions',
            render: (_, resume) => (
              <div className="flex items-center justify-end gap-2">
                {resume.finalInterviewStatus === 'shortlisted' && 
                 (!resume.offerLetterStatus || resume.offerLetterStatus === 'not_generated') && (
                  <button
                    onClick={() => onGenerateOffer(resume._id)}
                    disabled={generatingOffer === resume._id}
                    className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                  >
                    {generatingOffer === resume._id ? 'Generating...' : 'Generate Offer'}
                  </button>
                )}
                {(resume.offerLetterStatus === 'generated' || resume.offerLetterStatus === 'sent') && 
                 resume.offerLetter && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setSelectedOfferLetter({ content: resume.offerLetter.content, company: resume.offerLetter.company || null });
                        setShowOfferLetter(true);
                      }}
                      className="text-gray-600 hover:text-gray-800"
                      title="View Offer Letter"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onSendOffer(resume._id)}
                      disabled={loading || resume.offerLetterStatus === 'sent'}
                      className="text-green-600 hover:text-green-800 disabled:opacity-50"
                      title="Send Offer Letter"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            )
          }
        ];
    }
  };

  const columns = getColumns();

  // Function to check and update status based on score
  const checkAndUpdateStatus = useCallback(async (resume) => {
    // Get the relevant score, status and passing score based on screenType
    let candidateScore, passingScore, inviteStatus;
    
    switch(screenType) {
      case 'preScreening':
        candidateScore = resume.preScreeningScore;
        passingScore = jobDetails?.screeningConfig?.preScreening?.passingScore;
        inviteStatus = resume.inviteStatus?.preScreening;
        break;
      case 'psychoanalytical':
        candidateScore = resume.psychoanalyticalScore;
        passingScore = jobDetails?.screeningConfig?.psychoanalytical?.passingScore;
        inviteStatus = resume.inviteStatus?.psychoanalytical;
        break;
      case 'finalInterview':
        candidateScore = resume.finalInterviewScore;
        passingScore = jobDetails?.screeningConfig?.finalInterview?.passingScore;
        inviteStatus = resume.inviteStatus?.finalInterview;
        break;
      default:
        return;
    }

    // Add debug logs
    console.log('Checking status for:', {
      screenType,
      candidateScore,
      passingScore,
      inviteStatus,
      currentStatus: resume[`${screenType}Status`]
    });

    // Only proceed if:
    // 1. The assessment is completed
    // 2. We have a numeric score
    // 3. We have a passing score configured
    if (inviteStatus !== 'completed' || 
        typeof candidateScore !== 'number' || 
        !passingScore) {
      console.log('Skipping status update:', {
        reason: {
          notCompleted: inviteStatus !== 'completed',
          noScore: typeof candidateScore !== 'number',
          noPassingScore: !passingScore
        }
      });
      return;
    }

    // Check if status is overridden
    const isOverridden = screenType === 'preScreening' ? resume.preScreeningOverride : 
                        screenType === 'psychoanalytical' ? resume.psychoanalyticalOverride : 
                        screenType === 'finalInterview' ? resume.finalInterviewOverride : false;

    if (isOverridden) {
      console.log('Status is overridden, skipping update');
      return;
    }

    // Determine correct status based on score
    const expectedStatus = candidateScore >= passingScore ? 'shortlisted' : 'rejected';
    const currentStatus = resume[`${screenType}Status`];

    console.log('Status comparison:', {
      expectedStatus,
      currentStatus,
      shouldUpdate: currentStatus !== expectedStatus
    });

    // If status doesn't match expected and not overridden, update it
    if (currentStatus !== expectedStatus) {
      await onStatusChange(resume._id, screenType, expectedStatus, false);
    }
  }, [screenType, jobDetails, onStatusChange]);

  // Check scores and update statuses on component mount and when resumes/jobDetails change
  useEffect(() => {
    const LEGACY_TYPES = ['preScreening', 'psychoanalytical', 'finalInterview'];
    if (!LEGACY_TYPES.includes(screenType)) return;
    if (resumes.length &&
        jobDetails &&
        jobDetails.screeningConfig &&
        jobDetails.screeningConfig[screenType]?.passingScore) {
      resumes.forEach(resume => {
        checkAndUpdateStatus(resume);
      });
    }
  }, [resumes, jobDetails, screenType, checkAndUpdateStatus]);

  // Modify the status change handler to always send override true for manual changes
  const handleStatusChange = async (resumeId, type, newStatus) => {
    try {
      // Always set override to true for manual status changes
      await onStatusChange(resumeId, type, newStatus, true);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const renderCellContent = (column, resume) => {
    if (column.type === 'status') {
      return (
        <StatusDropdown
          value={resume[`${screenType}Status`]}
          onChange={(newStatus) => handleStatusChange(resume._id, screenType, newStatus)}
          options={column.options}
          disabled={loading}
        />
      );
    }

    if (column.render) {
      return column.render(resume[column.key], resume);
    }

    return resume[column.key];
  };

  const handleScoreEdit = (resume, type) => {
    let currentScore;
    if (type === 'preScreening') {
      currentScore = resume.preScreeningScore;
    } else if (type === 'psychoanalytical') {
      currentScore = resume.psychoanalyticalScore;
    } else if (type === 'finalInterview') {
      currentScore = resume.finalInterviewScore;
    }
    setEditingScore({ id: resume._id, type });
    setScoreInput(currentScore?.toString() || '');
  };

  const handleScoreSubmit = async (resumeId, type) => {
    try {
      const score = parseInt(scoreInput);
      if (isNaN(score) || score < 0 || score > 100) {
        toast.error('Please enter a valid score between 0 and 100');
        return;
      }
      
      // Get jobId from the current context or props
      const jobId = jobDetails?._id || window.location.pathname.slice(APP_BASE.length).split('/')[2];
      
      if (!jobId) {
        toast.error('Job ID not found. Please refresh the page and try again.');
        return;
      }
      
      await resumeApi.updateTestScore(resumeId, jobId, score, type);
      toast.success('Score updated successfully');
      
      // Update the local state immediately for better UX
      if (onScoreUpdate) {
        await onScoreUpdate(resumeId, score, type);
      }
      
      // Clear editing state
      setEditingScore(null);
      setScoreInput('');
    } catch (error) {
      console.error('Error updating score:', error);
      toast.error(error.message || 'Failed to update score');
    }
  };

  const renderScoreCell = (value, resume, type) => {
    const isEditing = editingScore?.id === resume._id && editingScore?.type === type;
    
    // Only allow editing for final interview scores
    const canEdit = type === 'finalInterview';

    if (isEditing && canEdit) {
      return (
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0"
            max="100"
            value={scoreInput}
            onChange={(e) => setScoreInput(e.target.value)}
            className="w-20 px-2 py-1 border rounded"
            autoFocus
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleScoreSubmit(resume._id, type);
              }
            }}
          />
          <button
            onClick={() => handleScoreSubmit(resume._id, type)}
            className="text-green-600 hover:text-green-800"
          >
            ✓
          </button>
          <button
            onClick={() => setEditingScore(null)}
            className="text-red-600 hover:text-red-800"
          >
            ✕
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center gap-2">
        <span className="px-2 py-1 text-sm rounded bg-blue-100 text-blue-800">
          {value || 0}
        </span>
        {canEdit && (
          <button
            onClick={() => handleScoreEdit(resume, type)}
            className="text-gray-400 hover:text-gray-600"
          >
            <Edit2 className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  };

  const handleReprocessResume = async (resume) => {
    try {
      console.log('Reprocessing resume:', {
        id: resume._id,
        fileName: resume.fileName,
        jobId: resume.jobId,
        processingStatus: resume.processingStatus
      });
      
      // Check if resume has a valid ID
      if (!resume._id) {
        toast.error('Invalid resume ID');
        return;
      }
      
      setReprocessingResume(resume._id);
      const result = await onReprocess(resume._id);
      console.log('Reprocess result:', result);
      toast.success(`Resume "${resume.fileName}" reprocessed successfully`);
    } catch (error) {
      console.error('Error reprocessing resume:', error);
      console.error('Resume that failed:', {
        id: resume._id,
        fileName: resume.fileName,
        jobId: resume.jobId
      });
      toast.error(error.message || 'Failed to reprocess resume');
    } finally {
      setReprocessingResume(null);
    }
  };

  // Removed handleInviteClick - not used

  const handleViewPsychoSession = async (resume) => {
    setPsychoSessionLoading(true);
    setShowPsychoSessionModal(true);
    setPsychoSessionError(null);
    try {
      const { data } = await api.get(`/psychoanalytical/session/${resume._id}`);
      setPsychoSessionData(data.data);
    } catch (err) {
      setPsychoSessionError('Failed to load session details');
    } finally {
      setPsychoSessionLoading(false);
    }
  };

  const handleResetPsychoLink = async (resume) => {
    // Implement reset logic (e.g., call backend to reset link)
    try {
      await resumeApi.resetTestAttempt(resume._id, 'psychoanalytical');
      toast.success('Interview link reset successfully');
      setShowPsychoSessionModal(false);
      // Refresh the data after reset
      if (onRefresh) {
        await onRefresh();
      }
    } catch (err) {
      console.error('Error resetting psychoanalytical link:', err);
      toast.error('Failed to reset link');
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      if (onRefresh) {
        await onRefresh();
      } else {
        // Fallback: trigger page change to refresh data
        if (onPageChange) {
          await onPageChange(pagination.page, currentFilter);
        }
      }
      toast.success('Resume data refreshed successfully');
    } catch (error) {
      console.error('Error refreshing resume data:', error);
      toast.error('Failed to refresh resume data');
    } finally {
      setRefreshing(false);
    }
  };

  const handleEditHRFeedback = async (resume) => {
    let feedbackValue = resume.finalInterviewResult?.hrFeedback?.feedback || '';
    
    Modal.confirm({
      title: 'HR Feedback',
      content: (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Add your feedback for this candidate:
          </label>
          <textarea
            id="hr-feedback-textarea"
            rows={4}
            className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            defaultValue={feedbackValue}
            placeholder="Enter your feedback about the candidate's performance..."
            onChange={(e) => {
              feedbackValue = e.target.value;
            }}
            onKeyDown={(e) => {
              // Prevent form submission on Enter key
              if (e.key === 'Enter' && e.ctrlKey) {
                e.preventDefault();
                return false;
              }
            }}
          />
          <div className="mt-2 text-sm text-gray-500">
            Press Ctrl+Enter to submit, or use the Save button below.
          </div>
        </div>
      ),
      okText: 'Save Feedback',
      cancelText: 'Cancel',
      width: 500,
      onOk: () => {
        return new Promise(async (resolve, reject) => {
          try {
            const textareaElement = document.getElementById('hr-feedback-textarea');
            const feedback = textareaElement ? textareaElement.value : feedbackValue;
            
            if (!feedback || !feedback.trim()) {
              toast.error('Please enter feedback before saving');
              reject(new Error('Feedback is required'));
              return;
            }

            // Validate feedback length
            if (feedback.trim().length < 10) {
              toast.error('Feedback must be at least 10 characters long');
              reject(new Error('Feedback too short'));
              return;
            }

            if (feedback.trim().length > 1000) {
              toast.error('Feedback must be less than 1000 characters');
              reject(new Error('Feedback too long'));
              return;
            }

            await resumeApi.updateHRFeedback(resume._id, feedback.trim());
            toast.success('HR Feedback updated successfully');
            
            // Refresh the data to show updated feedback
            if (onRefresh) {
              await onRefresh();
            } else {
              // Fallback to page reload if onRefresh is not available
              window.location.reload();
            }
            
            resolve();
          } catch (error) {
            console.error('Error updating HR feedback:', error);
            toast.error(error.message || 'Failed to update HR Feedback');
            reject(error);
          }
        });
      },
    });
  };

  return (
    <div className="space-y-4">
      {showResumeViewer && selectedResume && (
        <ResumeViewer 
          resume={selectedResume}
          onClose={() => {
            setShowResumeViewer(false);
            setSelectedResume(null);
          }}
        />
      )}

      {/* Action Buttons for Resume Screening */}
      {screenType === 'resumeScreening' && resumes.length > 0 && (
        <div className="flex justify-end gap-3 mb-4">
          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh resume data and processing status"
          >
            {refreshing ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            {refreshing ? 'Refreshing...' : 'Refresh Status'}
          </button>
        </div>
      )}

      {(!resumes || resumes.length === 0) && (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
          <p className="text-lg font-medium">No resumes found</p>
          <p className="text-sm text-gray-400">
                        { 
                         screenType === 'preScreening'
                            ? 'Shortlist candidates to see them here'
                            : screenType === 'psychoanalytical'
                                ? 'Complete pre-screening to see candidates here'
                                : screenType === 'finalInterview'
                                    ? 'Complete psychoanalytical test to see candidates here'
                                    : 'Complete final interview to see candidates here'}
                    </p>
        </div>
      )}

      {resumes.length > 0 && (
        <div className="overflow-x-auto mt-6">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  #
                </th>
                {columns.map((column, index) => (
                  <th 
                    key={column.key}
                    className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider
                      ${index === 0 ? 'text-left' : 'text-center'}`}
                  >
                    {column.header}
                  </th>
                ))}
                {/* <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th> */}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {resumes.map((resume, index) => (
                <tr key={resume._id}>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                    {(pagination.page - 1) * pagination.limit + index + 1}
                  </td>
                  {columns.map((column, colIndex) => (
                    <td 
                      key={column.key} 
                      className={`px-6 py-4 ${colIndex === 0 ? 'text-left' : 'text-center'}`}
                    >
                      {renderCellContent(column, resume)}
                    </td>
                  ))}
                  {/* <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                    {showDelete ? (
                      <button
                        onClick={() => onDelete(resume._id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    ) : (
                      <button
                        onClick={() => handleInviteClick(resume)}
                        className={`text-blue-600 hover:text-blue-800`}
                      >
                        {resume.inviteStatus?.[screenType] === 'sent' || 
                         resume.inviteStatus?.[screenType] === 'completed' 
                          ? 'Resend' 
                          : 'Send Invite'}
                      </button>
                    )}
                  </td> */}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {resumes.length > 0 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages || Math.ceil(pagination.total / pagination.limit)}
          totalItems={pagination.total || 0}
          itemsPerPage={pagination.limit}
          onPageChange={onPageChange}
        />
      )}

      <OfferLetterModal
        isOpen={showOfferLetter}
        onClose={() => { setShowOfferLetter(false); setSelectedOfferLetter(null); }}
        content={selectedOfferLetter?.content || selectedOfferLetter}
        company={selectedOfferLetter?.company || null}
      />

      <InterviewDetailsModal
        isOpen={showInterviewDetails}
        onClose={() => setShowInterviewDetails(false)}
        resume={selectedInterviewData.resume}
        interviewType={selectedInterviewData.type}
      />

      {/* ── Edit Offer Letter Modal ── */}
      {editOfferState && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/40" onClick={() => !savingOffer && setEditOfferState(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 p-6 z-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Edit Offer Letter</h3>
              <button onClick={() => setEditOfferState(null)} disabled={savingOffer}
                className="p-1.5 hover:bg-gray-100 rounded-full disabled:opacity-50">
                <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-xs text-gray-400 mb-3">Edit the markdown content directly. Changes will be saved to the candidate's offer letter.</p>
            <textarea
              value={editOfferState.content}
              onChange={e => setEditOfferState(prev => ({ ...prev, content: e.target.value }))}
              rows={16}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setEditOfferState(null)} disabled={savingOffer}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                Cancel
              </button>
              <button
                disabled={savingOffer}
                onClick={async () => {
                  try {
                    setSavingOffer(true);
                    await api.put(`/resumes/${editOfferState.resumeId}/offer-letter`, {
                      content: editOfferState.content
                    });
                    toast.success('Offer letter updated');
                    setEditOfferState(null);
                    if (onEditOffer) onEditOffer(editOfferState.resumeId, editOfferState.content);
                  } catch (e) {
                    toast.error('Failed to save changes');
                  } finally {
                    setSavingOffer(false);
                  }
                }}
                className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600 disabled:opacity-50"
              >
                {savingOffer ? <><RefreshCw className="w-4 h-4 animate-spin" /> Saving…</> : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPsychoSessionModal && (
        <Modal open={showPsychoSessionModal} onCancel={() => setShowPsychoSessionModal(false)} footer={null} width={800}>
          {psychoSessionLoading ? (
            <div className="py-8 text-center">Loading session details...</div>
          ) : psychoSessionError ? (
            <div className="py-8 text-center text-red-600">{psychoSessionError}</div>
          ) : psychoSessionData ? (
            <div>
              <h2 className="text-xl font-bold mb-2">Psychoanalytical Interview Session</h2>
              <div className="mb-2 text-sm text-gray-600">Session ID: {psychoSessionData.sessionId}</div>
              <div className="mb-2 text-sm text-gray-600">Status: {psychoSessionData.status}</div>
              <div className="mb-2 text-sm text-gray-600">Started: {psychoSessionData.startedAt ? new Date(psychoSessionData.startedAt).toLocaleString() : 'N/A'}</div>
              <div className="mb-2 text-sm text-gray-600">Completed: {psychoSessionData.completedAt ? new Date(psychoSessionData.completedAt).toLocaleString() : 'N/A'}</div>
              <div className="mb-2 text-sm text-gray-600">Thread ID: {psychoSessionData.threadId}</div>
              <div className="mb-4">
                <button
                  onClick={() => handleResetPsychoLink(psychoSessionData)}
                  className="px-3 py-1 bg-orange-500 text-white rounded hover:bg-orange-600"
                >
                  Reset Link
                </button>
              </div>
              <div className="mb-4">
                <h3 className="font-semibold mb-2">Questions & Answers</h3>
                <ul className="space-y-2 max-h-60 overflow-y-auto">
                  {psychoSessionData.questions?.map((q, idx) => (
                    <li key={idx} className="border rounded p-2">
                      <div className="font-medium text-gray-800">Q{idx + 1}: {q.question}</div>
                      {q.answer && <div className="text-green-700 mt-1">A: {q.answer}</div>}
                    </li>
                  ))}
                </ul>
              </div>
              {psychoSessionData.result && (
                <div className="mb-2">
                  <h3 className="font-semibold mb-2">Result</h3>
                  <div>Score: {psychoSessionData.result?.score || 'N/A'}</div>
                  <div>Feedback: {psychoSessionData.result?.feedback || 'N/A'}</div>
                </div>
              )}
            </div>
          ) : null}
        </Modal>
      )}
    </div>
  );
};

export default ResumesContent;
